import { Link } from "react-router-dom";
import { CheckCircle2, ArrowRight, ShieldCheck, Heart, Users } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { CqcBadge } from "@/components/CqcBadge";
import { CqcWidget } from "@/components/CqcWidget";
import { SITE, OUTCOMES, TRAINING_AREAS } from "@/lib/site";
import { ABOUT_PAGE } from "@/constants/testIds";

export default function About() {
  return (
    <div data-testid={ABOUT_PAGE.root}>
      <PageHero
        eyebrow="About Embrace Healthcare"
        title={
          <>
            We believe everyone deserves to live{" "}
            <em className="not-italic text-brand">safely and independently</em>{" "}
            at home.
          </>
        }
        intro="Choosing care is an emotional decision. Our experienced team works closely with service users, families, social workers, healthcare professionals and commissioners to design flexible support that promotes independence, dignity, choice and wellbeing."
        image={SITE.imagery.aboutTrust}
        imageAlt="Embrace Healthcare carer in purple uniform supporting an elderly lady at home"
      />

      {/* MISSION */}
      <section
        data-testid={ABOUT_PAGE.mission}
        className="py-20 lg:py-28"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <span className="inline-flex items-center gap-2 text-xs tracking-[0.22em] uppercase font-semibold text-brand">
              <span className="w-8 h-px bg-brand" /> Our mission
            </span>
            <h2 className="font-display text-3xl lg:text-5xl mt-5 leading-tight">
              Care that{" "}
              <em className="not-italic text-brand">embraces</em> the
              individual.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-5 text-lg leading-relaxed text-brand-mute">
            <p>
              At Embrace Healthcare we deliver high-quality, compassionate and
              person-centred care tailored around each individual's unique
              needs, preferences, goals and lifestyle.
            </p>
            <p>
              Whether you require occasional support with domestic tasks, help
              with personal care, medication support, companionship,
              specialist care for complex conditions, or comprehensive daily
              assistance — we provide a service that is built entirely around
              you.
            </p>
            <p>
              Our goal is not simply to provide care, but to empower people to
              remain active, engaged and in control of their daily lives.
            </p>
          </div>
        </div>
      </section>

      {/* TRAINING AREAS */}
      <section
        data-testid={ABOUT_PAGE.values}
        className="py-20 lg:py-28 bg-brand-cream"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end mb-14">
            <div className="lg:col-span-7">
              <span className="inline-flex items-center gap-2 text-xs tracking-[0.22em] uppercase font-semibold text-brand">
                <span className="w-8 h-px bg-brand" /> Highly trained care professionals
              </span>
              <h2 className="font-display text-3xl lg:text-5xl mt-5 leading-tight">
                Specialist training. Continuous professional development.
              </h2>
            </div>
            <div className="lg:col-span-5">
              <p className="text-lg text-brand-mute leading-relaxed">
                Our staff complete comprehensive induction and ongoing training
                across the areas that matter most in domiciliary care.
              </p>
            </div>
          </div>

          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {TRAINING_AREAS.map((t) => (
              <li
                key={t.slug}
                className="flex items-center gap-3 bg-white border border-brand-line rounded-2xl px-5 py-4"
              >
                <span className="flex items-center justify-center w-8 h-8 rounded-full bg-brand-soft text-brand">
                  <CheckCircle2 className="w-4 h-4" strokeWidth={2} />
                </span>
                <span className="font-semibold text-brand-ink">{t.label}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* OUTCOMES */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 text-xs tracking-[0.22em] uppercase font-semibold text-brand">
              <span className="w-8 h-px bg-brand" /> Supporting better outcomes
            </span>
            <h2 className="font-display text-3xl lg:text-5xl mt-5 leading-tight">
              Our care enables people to…
            </h2>
          </div>
          <ul className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-4">
            {OUTCOMES.map((o) => (
              <li key={o} className="flex items-start gap-3 text-brand-ink">
                <CheckCircle2 className="w-5 h-5 text-brand mt-1 shrink-0" />
                <span className="text-lg leading-relaxed">{o}</span>
              </li>
            ))}
          </ul>

          <aside
            data-testid={ABOUT_PAGE.cqc}
            className="mt-16 lg:mt-20 bg-brand text-white rounded-3xl p-10 lg:p-14 relative overflow-hidden"
          >
            <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-white/15 blur-3xl pointer-events-none" />
            <div className="absolute -bottom-24 -left-16 w-80 h-80 rounded-full bg-brand-deep/35 blur-3xl pointer-events-none" />
            <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
              <div className="lg:col-span-7">
                <ShieldCheck className="w-10 h-10 text-white mb-5" />
                <h3
                  style={{ color: "#ffffff" }}
                  className="font-display text-3xl lg:text-4xl leading-tight"
                >
                  Safe, quality-assured care
                </h3>
                <p className="mt-5 text-white/90 leading-relaxed text-lg">
                  Embrace Healthcare is fully regulated by the Care Quality
                  Commission (CQC) and operates in accordance with national
                  standards and best-practice guidance. We are committed to
                  safeguarding vulnerable adults and continuously monitoring and
                  improving our services.
                </p>
                <div className="mt-7 flex flex-wrap gap-3">
                  <CqcBadge variant="dark" />
                </div>
                <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="bg-white/15 rounded-2xl p-4 border border-white/20">
                    <Heart className="w-5 h-5 text-white mb-2" />
                    <p className="text-sm text-white">
                      Person-centred at every visit
                    </p>
                  </div>
                  <div className="bg-white/15 rounded-2xl p-4 border border-white/20">
                    <Users className="w-5 h-5 text-white mb-2" />
                    <p className="text-sm text-white">
                      Consistent, familiar care teams
                    </p>
                  </div>
                </div>
                <Link
                  to="/contact"
                  className="mt-8 inline-flex items-center gap-2 rounded-full bg-white text-brand px-6 py-3 text-sm font-semibold hover:bg-brand-cream transition"
                >
                  Get in touch <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
              <div className="lg:col-span-5">
                <CqcWidget />
              </div>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
